import { sendSMS, formatPhoneNumber } from '../lib/gohighlevel';
import { supabase } from '../lib/supabase';
import { saveRateConfirmationToDriverFolder } from '../utils/rateConfirmationStorage';

export interface DispatchData {
  id: string;
  dispatchNumber: string;
  loadNumber: string;
  driverId: string;
  driverName: string;
  driverPhone: string;
  truckId: string;
  truckNumber: string;
  pickupLocation: string;
  pickupCity: string;
  pickupState: string;
  pickupTime: string;
  deliveryLocation: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryTime: string;
  distance: number;
  rate: number;
  status: 'assigned' | 'in-transit' | 'delivered' | 'cancelled';
  assignedAt: string;
  assignedBy: string;
  notes?: string;
}

/**
 * Generate a unique dispatch number
 */
function generateDispatchNumber(): string {
  const prefix = 'DIS';
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}${random}`;
}

/**
 * Create a new dispatch when a load is assigned
 */
export async function createDispatch(data: {
  loadNumber: string;
  driverId: string;
  driverName: string;
  driverPhone: string;
  truckId: string;
  truckNumber: string;
  pickupLocation: string;
  pickupCity: string;
  pickupState: string;
  pickupTime: string;
  deliveryLocation: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryTime: string;
  distance: number;
  rate: number;
  assignedBy: string;
  status?: string;
  driverPay?: number;
  emptyMiles?: number;
  notes?: string;
  rateConfirmationPath?: string; // Path to the rate confirmation PDF in storage
}): Promise<DispatchData> {
  const dispatchNumber = generateDispatchNumber();

  // Create the dispatch ATOMICALLY. The dispatch-row insert and the load status
  // flip ('booked' -> 'dispatched' + driver/truck) run inside a single Postgres
  // transaction (create_dispatch_atomic). They commit together or not at all, so
  // an interrupted call (closed modal, dropped request) can never leave an orphan
  // 'active' dispatch next to a still-'booked' load — the bug that stranded loads
  // on the board. The function also self-heals a leftover orphan from an older
  // failed attempt. See migration 20260626000000_create_dispatch_atomic.sql.
  let dispatchData: any;

  const { data: rpcRow, error: rpcError } = await supabase.rpc('create_dispatch_atomic', {
    p_load_number: data.loadNumber,
    p_dispatch_number: dispatchNumber,
    p_driver_id: data.driverId,
    p_truck_id: data.truckId,
    p_status: data.status || 'active',
    p_dispatch_date: data.pickupTime || null,
    p_driver_pay: data.driverPay || 0,
    p_empty_miles: data.emptyMiles || 0,
  });

  if (rpcError) {
    // Tell "function not deployed yet" apart from a real failure, so we can fall
    // back gracefully during the window between code deploy and migration apply.
    const msg = rpcError.message || '';
    const functionMissing =
      (rpcError as any).code === 'PGRST202' ||
      /create_dispatch_atomic/i.test(msg) ||
      /could not find the function|schema cache/i.test(msg);

    if (!functionMissing) {
      // Real DB failure (e.g. a genuine duplicate). Surface the real message
      // instead of a generic one so dispatchers see why.
      console.error('Atomic dispatch failed:', rpcError);
      throw new Error(msg || 'Dispatch could not be created. Please try again.');
    }

    console.warn(
      'create_dispatch_atomic not found — using legacy write path. Apply migration ' +
        '20260626000000_create_dispatch_atomic.sql to enable atomic dispatch.'
    );
    dispatchData = await legacyCreateDispatchWrites(data, dispatchNumber);
  } else {
    dispatchData = rpcRow;
  }

  const loadId = dispatchData.load_id;

  // Fire the dispatch-created email reminder (best-effort, non-blocking).
  if (dispatchData) {
    const { emailReminders } = await import('./emailReminders');
    void emailReminders.dispatchCreated({
      id: dispatchData.id,
      dispatch_number: dispatchNumber,
      load_number: data.loadNumber,
      driver_pay: data.driverPay,
    });
  }

  // Copy rate confirmation PDF to driver's folder
  try {
    console.log('🔍 Checking for rate confirmation PDF to copy...');
    let rateConfPath = data.rateConfirmationPath;
    
    // If no path provided, check if load has a rate confirmation document
    if (!rateConfPath) {
      console.log('🔍 No rateConfirmationPath provided, checking load_documents...');
      const { data: loadDocs, error: loadDocsError } = await supabase
        .from('load_documents')
        .select(`
          documents (
            file_path
          )
        `)
        .eq('load_id', loadId)
        .eq('document_type', 'rate_confirmation')
        .limit(1)
        .single();
      
      if (loadDocsError) {
        console.log('⚠️ Error querying load_documents:', loadDocsError);
      }
      
      console.log('📄 Load documents query result:', loadDocs);
      
      if (loadDocs?.documents) {
        rateConfPath = (loadDocs.documents as any).file_path;
        console.log('✅ Found rate confirmation PDF:', rateConfPath);
      } else {
        console.log('⚠️ No rate confirmation document found for this load');
      }
    }
    
    // If we have a rate confirmation path, save it to driver's folder
    if (rateConfPath) {
      console.log('📁 Copying rate confirmation to driver folder...');
      console.log('   Driver:', data.driverName);
      console.log('   Pickup Date:', data.pickupTime);
      console.log('   Load Number:', data.loadNumber);
      
      const pickupDate = new Date(data.pickupTime);
      const result = await saveRateConfirmationToDriverFolder({
        originalFilePath: rateConfPath,
        driverName: data.driverName,
        pickupDate: pickupDate,
        loadNumber: data.loadNumber,
      });
      
      if (result.success) {
        console.log(`✅ Rate confirmation saved to driver folder: ${result.newFilePath}`);
      } else {
        console.error(`⚠️ Failed to save rate confirmation to driver folder: ${result.error}`);
      }
    } else {
      console.log('ℹ️ No rate confirmation PDF to copy');
    }
  } catch (error) {
    console.error('⚠️ Error saving rate confirmation to driver folder:', error);
    // Don't fail the dispatch creation if PDF saving fails
  }

  const dispatch: DispatchData = {
    id: dispatchData.id,
    dispatchNumber,
    loadNumber: data.loadNumber,
    driverId: data.driverId,
    driverName: data.driverName,
    driverPhone: data.driverPhone,
    truckId: data.truckId,
    truckNumber: data.truckNumber,
    pickupLocation: data.pickupLocation,
    pickupCity: data.pickupCity,
    pickupState: data.pickupState,
    pickupTime: data.pickupTime,
    deliveryLocation: data.deliveryLocation,
    deliveryCity: data.deliveryCity,
    deliveryState: data.deliveryState,
    deliveryTime: data.deliveryTime,
    distance: data.distance,
    rate: data.rate,
    status: data.status || 'active',
    assignedAt: new Date().toISOString(),
    assignedBy: data.assignedBy,
    notes: data.notes,
  };

  // SMS notifications disabled per user request
  console.log('ℹ️ SMS notifications are disabled for dispatch creation');

  // Trigger event for real-time updates
  window.dispatchEvent(new CustomEvent('dispatchCreated', { detail: dispatch }));

  return dispatch;
}

/**
 * Legacy two-write fallback, used ONLY when create_dispatch_atomic is not yet
 * deployed to the database. This is the exact previous path — insert dispatch,
 * then update load, with the compensating rollback if the load update fails —
 * so behavior during the deploy window is identical to before (no regression).
 * Once the migration is applied, this is never reached.
 */
async function legacyCreateDispatchWrites(
  data: Parameters<typeof createDispatch>[0],
  dispatchNumber: string
): Promise<any> {
  const { data: loadData } = await supabase
    .from('loads')
    .select('id')
    .eq('load_number', data.loadNumber)
    .single();

  if (!loadData) {
    throw new Error('Load not found');
  }

  const { data: existingDispatch } = await supabase
    .from('dispatches')
    .select('dispatch_number')
    .eq('load_id', loadData.id)
    .eq('status', 'active')
    .limit(1)
    .maybeSingle();

  if (existingDispatch) {
    throw new Error(
      `Load ${data.loadNumber} already has an active dispatch (${existingDispatch.dispatch_number}).`
    );
  }

  const { data: { user } } = await supabase.auth.getUser();

  const { data: dispatchData, error } = await supabase
    .from('dispatches')
    .insert({
      dispatch_number: dispatchNumber,
      load_id: loadData.id,
      driver_id: data.driverId,
      truck_id: data.truckId,
      status: data.status || 'active',
      dispatch_date: data.pickupTime,
      driver_pay: data.driverPay || 0,
      empty_miles: data.emptyMiles || 0,
      created_by: user?.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating dispatch:', error);
    throw error;
  }

  const { data: updatedLoad, error: loadUpdateError } = await supabase
    .from('loads')
    .update({
      status: 'dispatched',
      driver_id: data.driverId,
      truck_id: data.truckId,
    })
    .eq('id', loadData.id)
    .select('id')
    .maybeSingle();

  if (loadUpdateError || !updatedLoad) {
    console.error('Failed to update load after creating dispatch, rolling back:', loadUpdateError);
    await supabase.from('dispatches').delete().eq('id', dispatchData.id);
    throw new Error(
      `Dispatch could not be completed: the load status update failed${loadUpdateError ? ` (${loadUpdateError.message})` : ' (load not updated)'}. No dispatch was created — please try again.`
    );
  }

  return dispatchData;
}

/**
 * Get all dispatches
 */
export function getDispatches(): DispatchData[] {
  const stored = localStorage.getItem('dispatches');
  return stored ? JSON.parse(stored) : [];
}

/**
 * Get dispatch by ID
 */
export function getDispatchById(id: string): DispatchData | null {
  const dispatches = getDispatches();
  return dispatches.find(d => d.id === id) || null;
}

/**
 * Get dispatches by status
 */
export function getDispatchesByStatus(status: DispatchData['status']): DispatchData[] {
  const dispatches = getDispatches();
  return dispatches.filter(d => d.status === status);
}

/**
 * Get active dispatches (assigned or in-transit)
 */
export function getActiveDispatches(): DispatchData[] {
  const dispatches = getDispatches();
  return dispatches.filter(d => d.status === 'assigned' || d.status === 'in-transit');
}

/**
 * Update dispatch status
 */
export function updateDispatchStatus(
  dispatchId: string,
  status: DispatchData['status']
): DispatchData | null {
  const dispatches = getDispatches();
  const index = dispatches.findIndex(d => d.id === dispatchId);
  
  if (index === -1) return null;
  
  dispatches[index].status = status;
  localStorage.setItem('dispatches', JSON.stringify(dispatches));
  
  // Trigger event for real-time updates
  window.dispatchEvent(new CustomEvent('dispatchUpdated', { detail: dispatches[index] }));
  
  return dispatches[index];
}

/**
 * Cancel dispatch
 */
export function cancelDispatch(
  dispatchId: string,
  reason?: string
): DispatchData | null {
  const dispatch = updateDispatchStatus(dispatchId, 'cancelled');
  
  if (dispatch && reason) {
    const dispatches = getDispatches();
    const index = dispatches.findIndex(d => d.id === dispatchId);
    if (index !== -1) {
      dispatches[index].notes = `${dispatches[index].notes || ''}\nCancelled: ${reason}`.trim();
      localStorage.setItem('dispatches', JSON.stringify(dispatches));
    }
  }
  
  return dispatch;
}

/**
 * Get dispatches for a specific driver
 */
export function getDriverDispatches(driverId: string): DispatchData[] {
  const dispatches = getDispatches();
  return dispatches.filter(d => d.driverId === driverId);
}

/**
 * Get dispatches for a specific truck
 */
export function getTruckDispatches(truckId: string): DispatchData[] {
  const dispatches = getDispatches();
  return dispatches.filter(d => d.truckId === truckId);
}

/**
 * Get today's dispatches
 */
export function getTodayDispatches(): DispatchData[] {
  const dispatches = getDispatches();
  const today = new Date().toISOString().split('T')[0];
  
  return dispatches.filter(d => {
    const assignedDate = d.assignedAt.split('T')[0];
    return assignedDate === today;
  });
}

/**
 * Get dispatch statistics
 */
export function getDispatchStats() {
  const dispatches = getDispatches();
  
  return {
    total: dispatches.length,
    assigned: dispatches.filter(d => d.status === 'assigned').length,
    inTransit: dispatches.filter(d => d.status === 'in-transit').length,
    delivered: dispatches.filter(d => d.status === 'delivered').length,
    cancelled: dispatches.filter(d => d.status === 'cancelled').length,
    today: getTodayDispatches().length,
    totalRevenue: dispatches
      .filter(d => d.status === 'delivered')
      .reduce((sum, d) => sum + d.rate, 0),
    totalMiles: dispatches
      .filter(d => d.status === 'delivered')
      .reduce((sum, d) => sum + d.distance, 0),
  };
}
